import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import DateInput from '../ui/date-input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Trash2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { formatTime12h } from '../../utils/timeFormat';
import useBranchLabels from '../../hooks/useBranchLabels';
import TimeInput12h from '../ui/time-input-12h';

const emptyRow = () => ({
  subject_id: '',
  exam_date: '',
  start_time: '09:00',
  end_time: '12:00',
  max_marks: 100,
  passing_marks: 35,
  room: '',
});

const DatesheetEditor = ({ open, onOpenChange, examId, classId, className, readOnly, onSaved }) => {
  const { formatLabel } = useBranchLabels();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [rows, setRows] = useState([emptyRow()]);

  useEffect(() => {
    if (!open || !examId || !classId) return;
    loadData();
  }, [open, examId, classId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [dsRes, classesRes] = await Promise.all([
        api.get(`/exams/${examId}/datesheets/${classId}`),
        api.get('/classes').catch(() => ({ data: { data: [] } })),
      ]);
      const classList = classesRes.data?.data || [];
      const cls = classList.find((c) => String(c.id) === String(classId));
      const classSubjects = cls?.subjects || [];
      if (Array.isArray(classSubjects) && classSubjects.length > 0) {
        setSubjects(classSubjects);
      } else {
        try {
          const subRes = await api.get('/subjects');
          setSubjects(subRes.data.data || []);
        } catch {
          setSubjects([]);
        }
      }
      const schedules = dsRes.data?.data?.schedules || [];
      if (schedules.length > 0) {
        setRows(schedules.map((s) => ({
          subject_id: String(s.subject_id),
          exam_date: s.exam_date,
          start_time: (s.start_time || '09:00:00').slice(0, 5),
          end_time: (s.end_time || '12:00:00').slice(0, 5),
          max_marks: s.max_marks ?? 100,
          passing_marks: s.passing_marks ?? 35,
          room: s.room || '',
          subject_name: s.subject_name || '',
        })));
      } else {
        setRows([emptyRow()]);
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to load datesheet', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (idx, field, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const handleSave = async () => {
    const schedules = rows.filter((r) => r.subject_id && r.exam_date);
    if (schedules.length === 0) {
      toast({ title: 'Error', description: 'Add at least one subject with date', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/exams/${examId}/datesheets`, {
        class_id: Number(classId),
        schedules: schedules.map((r) => ({
          subject_id: Number(r.subject_id),
          exam_date: r.exam_date,
          start_time: r.start_time ? `${r.start_time}:00` : '09:00:00',
          end_time: r.end_time ? `${r.end_time}:00` : '12:00:00',
          max_marks: Number(r.max_marks) || 100,
          passing_marks: Number(r.passing_marks) || 35,
          room: r.room || null,
        })),
      });
      toast({ title: 'Success', description: 'Datesheet saved as draft' });
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to save datesheet';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-border text-foreground max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{readOnly ? 'View' : 'Edit'} Datesheet — {className}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>
        ) : (
          <div className="space-y-3">
            {!readOnly && (
              <div className="flex justify-end">
                <Button type="button" variant="outline" size="sm" onClick={() => setRows((p) => [...p, emptyRow()])}>
                  <Plus className="w-4 h-4 mr-1" /> Add row
                </Button>
              </div>
            )}
            {rows.map((row, idx) => (
              <div
                key={idx}
                className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end border border-border rounded-lg p-3"
              >
                <div className="sm:col-span-4">
                  <Label className="text-xs">Subject</Label>
                  {readOnly ? (
                    <p className="text-sm py-2">
                      {row.subject_name || subjects.find((s) => String(s.id) === String(row.subject_id))?.name || '—'}
                    </p>
                  ) : (
                    <Select value={row.subject_id} onValueChange={(v) => updateRow(idx, 'subject_id', v)}>
                      <SelectTrigger className="bg-muted border-border h-9"><SelectValue placeholder="Subject" /></SelectTrigger>
                      <SelectContent className="bg-muted border-border">
                        {subjects.map((s) => <SelectItem key={s.id} value={String(s.id)}>{formatLabel(s.name, s)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="sm:col-span-3">
                  <Label className="text-xs">Date</Label>
                  {readOnly ? (
                    <p className="text-sm py-2">{row.exam_date}</p>
                  ) : (
                    <DateInput value={row.exam_date} onChange={(v) => updateRow(idx, 'exam_date', v)}
                      className="bg-muted border-border h-9 w-full" />
                  )}
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Start</Label>
                  {readOnly ? (
                    <p className="text-sm py-2">{formatTime12h(row.start_time)}</p>
                  ) : (
                    <TimeInput12h value={row.start_time} onChange={(v) => updateRow(idx, 'start_time', v)} triggerClassName="bg-muted border-border h-9" />
                  )}
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">End</Label>
                  {readOnly ? (
                    <p className="text-sm py-2">{formatTime12h(row.end_time)}</p>
                  ) : (
                    <TimeInput12h value={row.end_time} onChange={(v) => updateRow(idx, 'end_time', v)} triggerClassName="bg-muted border-border h-9" />
                  )}
                </div>
                <div className="hidden sm:block sm:col-span-1" />

                <div className="sm:col-span-2">
                  <Label className="text-xs">Max</Label>
                  {readOnly ? (
                    <p className="text-sm py-2">{row.max_marks}</p>
                  ) : (
                    <Input type="number" value={row.max_marks} onChange={(e) => updateRow(idx, 'max_marks', e.target.value)}
                      className="bg-muted border-border h-9 w-full text-center tabular-nums" />
                  )}
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Pass</Label>
                  {readOnly ? (
                    <p className="text-sm py-2">{row.passing_marks}</p>
                  ) : (
                    <Input type="number" value={row.passing_marks} onChange={(e) => updateRow(idx, 'passing_marks', e.target.value)}
                      className="bg-muted border-border h-9 w-full text-center tabular-nums" />
                  )}
                </div>
                <div className="sm:col-span-7">
                  <Label className="text-xs">Room</Label>
                  {readOnly ? (
                    <p className="text-sm py-2">{row.room || '—'}</p>
                  ) : (
                    <Input value={row.room} onChange={(e) => updateRow(idx, 'room', e.target.value)} className="bg-muted border-border h-9 w-full" />
                  )}
                </div>
                <div className="sm:col-span-1 flex justify-end">
                  {!readOnly && rows.length > 1 && (
                    <Button type="button" variant="ghost" size="icon" onClick={() => setRows((p) => p.filter((_, i) => i !== idx))}>
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  )}
                </div>
              </div>
            ))}

            {!readOnly && (
              <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={handleSave} disabled={submitting}>
                {submitting ? 'Saving…' : 'Save as draft'}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DatesheetEditor;
